import { useEffect, useMemo, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Stack, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '@/stores/authStore';
import { RouteMap } from '@/routes/Routes';
import { useProjects } from '../hooks/useProjects';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';
import NotesHeader from '@/pages/MyNotes/components/NotesHeader/NotesHeader';
import NotesContent from '@/pages/MyNotes/components/NotesContent/NotesContent';
import { BETA_FOLDERS_FEATURE_ENABLED } from '../config';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';

const ProjectNotesPage = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useAuthStore();
  const { betaFeatures, isLoading: isPlanLoading } = useCurrentPlanFeatures();
  const isBetaTester = hasBetaProductName(userData) || !!betaFeatures.folders;

  const { projects, isLoading } = useProjects(isBetaTester && BETA_FOLDERS_FEATURE_ENABLED ? userData?.id : undefined);

  const setSelectedTags = useSearchFilterNotesStore((state) => state.setSelectedTags);
  const previousTagsRef = useRef<string[]>(useSearchFilterNotesStore.getState().selectedTags);

  useEffect(() => {
    return () => {
      setSelectedTags(previousTagsRef.current);
    };
  }, [setSelectedTags]);

  const project = useMemo(() => projects.find((item) => item.id === folderId), [projects, folderId]);

  useEffect(() => {
    if (project) {
      setSelectedTags(project.tags);
    } else if (!isLoading) {
      setSelectedTags([]);
    }
  }, [project, isLoading, setSelectedTags]);

  if (!BETA_FOLDERS_FEATURE_ENABLED) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  if (!hasBetaProductName(userData) && !betaFeatures.folders && !isPlanLoading) {
    return <Navigate to={RouteMap.BETA} replace />;
  }

  if (!folderId) {
    return <Navigate to={RouteMap.FOLDERS} replace />;
  }

  const handleBack = () => {
    navigate(RouteMap.FOLDERS);
  };

  return (
    <Stack alignItems="center" pt={4} pb={0} px={isMobile ? 2 : 4} spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Folders
        </Button>
        {!project && (
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading folder…' : 'Folder not found.'}
          </Typography>
        )}
      </Stack>
      <NotesHeader
        title={project ? project.name : 'Folder'}
        description={
          project
            ? project.tags.length > 0
              ? `Showing notes tagged with ${project.tags.join(', ')}.`
              : 'This folder does not have any tags yet.'
            : 'Select a folder from the sidebar to view matching notes.'
        }
      />
      <NotesContent />
    </Stack>
  );
};

export default ProjectNotesPage;
